import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { BulkActionBar } from '@/components/programming/BulkActionBar';
import { CalendarDateFilter } from '@/components/programming/CalendarDateFilter';
import { CalendarView } from '@/components/programming/CalendarView';
import { FilterBar, emptyFilterValues } from '@/components/programming/FilterBar';
import type { FilterValues } from '@/components/programming/FilterBar';
import { WorkoutCreatorMetadata } from '@/components/programming/WorkoutCreatorMetadata';
import { WorkoutDetail } from '@/components/programming/WorkoutDetail';
import { getCalendarDate } from '@/components/programming/calendarNavigation';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { API_BASE_URL } from '@/config';
import { useAdminApi } from '@/hooks/useAdminApi';
import { useSelection } from '@/hooks/useSelection';
import type { WorkoutAttribution } from '@ergshoot/shared';
import {
  AlertCircle,
  Calendar,
  CheckCircle,
  ChevronDown,
  FileText,
  ImageIcon,
  Layers,
  Loader2,
  Pencil,
  Plus,
  Trash2,
  Upload,
  X,
  XCircle,
} from 'lucide-react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { CsvBulkUploadDialog } from './programming-workbench/CsvBulkUploadDialog';
import { useCsvImport } from './programming-workbench/useCsvImport';

interface Track {
  id: string;
  name: string;
  description: string | null;
  is_active: boolean;
  is_coming_soon: boolean;
  logo_url: string | null;
  workout_count: number;
  created_at: string;
}

interface SpecialtyWorkout {
  id: string;
  name: string;
  description: string | null;
  movements: string[];
  scoring_type: string;
  scoring_direction: string;
  equipment: string[];
  scheduled_date: string;
  track: { id: string; name: string | null } | null;
  category?: string | null;
  coach_notes?: string | null;
  duration_minutes?: number | null;
  instructions?: string | null;
  attribution?: WorkoutAttribution | null;
  created_at?: string | null;
  created_by_username?: string | null;
}

export default function AdminTracks() {
  const adminApi = useAdminApi();

  // Data state
  const [tracks, setTracks] = useState<Track[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Selected track workouts state
  const [selectedTrackId, setSelectedTrackId] = useState<string | null>(null);
  const [trackWorkouts, setTrackWorkouts] = useState<SpecialtyWorkout[]>([]);
  const [isLoadingWorkouts, setIsLoadingWorkouts] = useState(false);
  const [workoutFilters, setWorkoutFilters] = useState<FilterValues>(emptyFilterValues);
  const [workoutViewMode, setWorkoutViewMode] = useState<'table' | 'calendar'>('table');
  const [calendarDateFilter, setCalendarDateFilter] = useState<string | null>(null);
  const [expandedWorkoutId, setExpandedWorkoutId] = useState<string | null>(null);

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingTrack, setEditingTrack] = useState<Track | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  // Form state
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [isComingSoon, setIsComingSoon] = useState(false);

  // Logo upload state
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Fetch tracks on mount
  const fetchTracks = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await adminApi.getTracks();

      if (response.success && response.data) {
        // Filter out core tracks — Specialty page only shows user-created programmes
        const CORE_TRACK_IDS = ['track-daily-wods', 'track-standards'];
        const CORE_TRACK_NAMES = ['daily wods', 'standards'];
        const specialtyTracks = response.data.tracks.filter(
          (t: { id: string; name: string }) =>
            !CORE_TRACK_IDS.includes(t.id) && !CORE_TRACK_NAMES.includes(t.name.toLowerCase())
        );
        setTracks(specialtyTracks);
      } else {
        setError(response.error || 'Failed to load tracks');
      }
    } catch (err) {
      console.error('Failed to fetch tracks:', err);
      setError('Failed to load tracks');
      toast.error('Failed to load tracks');
    } finally {
      setIsLoading(false);
    }
  }, [adminApi]);

  useEffect(() => {
    fetchTracks();
  }, [fetchTracks]);

  // Build logo display URL from track ID
  const getLogoUrl = (trackId: string) =>
    `${API_BASE_URL}/public/tracks/${trackId}/logo?t=${Date.now()}`;

  // Reset form
  const resetForm = () => {
    setName('');
    setDescription('');
    setIsActive(true);
    setIsComingSoon(false);
    setIsEditing(false);
    setEditingTrack(null);
    setLogoFile(null);
    setLogoPreview(null);
  };

  // Open modal for new track
  const handleAddTrack = () => {
    resetForm();
    setShowModal(true);
  };

  // Open modal for editing
  const handleEditTrack = (track: Track) => {
    setEditingTrack(track);
    setIsEditing(true);
    setName(track.name);
    setDescription(track.description || '');
    setIsActive(track.is_active);
    setIsComingSoon(track.is_coming_soon);
    setShowModal(true);
  };

  // Save track using adminApi methods
  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!name.trim()) {
      toast.error('Please enter a track name');
      return;
    }

    setIsSaving(true);

    try {
      let savedTrackId: string | null = null;

      if (isEditing && editingTrack) {
        // Update existing track
        const response = await adminApi.updateTrack(editingTrack.id, {
          name: name.trim(),
          description: description.trim() || undefined,
          is_active: isActive,
          is_coming_soon: isComingSoon,
        });

        if (response.success) {
          savedTrackId = editingTrack.id;
        } else {
          toast.error(response.error || 'Failed to update track');
        }
      } else {
        // Create new track
        const response = await adminApi.createTrack({
          name: name.trim(),
          description: description.trim() || undefined,
          is_coming_soon: isComingSoon,
        });

        if (response.success && response.data) {
          savedTrackId = response.data.track.id;
        } else {
          toast.error(response.error || 'Failed to create track');
        }
      }

      // Upload logo if a file was selected
      if (savedTrackId && logoFile) {
        setIsUploadingLogo(true);
        const uploadResponse = await adminApi.uploadTrackLogo(savedTrackId, logoFile);
        if (!uploadResponse.success) {
          toast.error(uploadResponse.error || 'Track saved but logo upload failed');
        }
      }

      if (savedTrackId) {
        toast.success(
          isEditing ? 'Specialty updated successfully' : 'Specialty created successfully'
        );
        setShowModal(false);
        resetForm();
        fetchTracks();
      }
    } catch (err) {
      console.error('Save error:', err);
      toast.error('Failed to save track');
    } finally {
      setIsSaving(false);
      setIsUploadingLogo(false);
    }
  };

  // Handle logo file selection
  const handleLogoSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate type
    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp', 'image/svg+xml'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Invalid file type. Please use PNG, JPEG, WebP, or SVG.');
      return;
    }

    // Validate size (2MB)
    if (file.size > 2 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 2MB.');
      return;
    }

    setLogoFile(file);
    // Create preview URL
    const reader = new FileReader();
    reader.onload = (ev) => {
      setLogoPreview(ev.target?.result as string);
    };
    reader.readAsDataURL(file);
  };

  // Selection state for bulk delete
  const selection = useSelection();
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);

  // Bulk delete tracks
  const handleBulkDelete = async () => {
    const ids = Array.from(selection.selectedIds);
    if (ids.length === 0) return;

    if (
      !confirm(
        `Are you sure you want to delete ${ids.length} specialty(ies)? This cannot be undone.`
      )
    ) {
      return;
    }

    setIsBulkDeleting(true);
    let deleted = 0;
    let skipped = 0;
    for (const id of ids) {
      try {
        const response = await adminApi.deleteTrack(id);
        if (response.success) {
          deleted++;
        } else {
          skipped++;
          toast.warning(response.error || `Failed to delete track ${id}`);
        }
      } catch {
        skipped++;
      }
    }
    if (deleted > 0) {
      toast.success(`Deleted ${deleted} specialty(ies)`);
    }
    if (skipped > 0) {
      toast.warning(`${skipped} could not be deleted (may have workouts)`);
    }
    selection.deselectAll();
    fetchTracks();
    setIsBulkDeleting(false);
  };

  // Bulk upload state
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadTrackId, setUploadTrackId] = useState<string>('');
  const csvImport = useCsvImport();

  const handleBulkUpload = () => {
    if (csvImport.csvContent.trim() && !uploadTrackId) {
      toast.error('Please select a specialty programme');
      return;
    }

    return csvImport.runUpload({
      upload: (csvContent) => adminApi.bulkUploadSpecialty(csvContent, uploadTrackId),
      onSuccess: (result) => {
        if (result.created > 0) {
          toast.success(`${result.created} workout(s) uploaded`);
          fetchTracks();
        }
        if (result.errors > 0) {
          toast.warning(`${result.errors} row(s) had errors`);
        }
      },
      failureMessage: 'Upload failed',
      unexpectedMessage: 'Failed to upload CSV',
    });
  };

  // Toggle track active status
  const handleToggleActive = async (track: Track) => {
    try {
      const response = await adminApi.updateTrack(track.id, {
        is_active: !track.is_active,
      });

      if (response.success) {
        toast.success(`Track ${track.is_active ? 'deactivated' : 'activated'} successfully`);
        fetchTracks();
      } else {
        toast.error(response.error || 'Failed to update track');
      }
    } catch (err) {
      console.error('Toggle error:', err);
      toast.error('Failed to update track');
    }
  };

  // Fetch workouts for selected track
  const fetchTrackWorkouts = useCallback(
    async (trackId: string) => {
      setIsLoadingWorkouts(true);
      try {
        const response = await adminApi.getProgramming({
          track_id: trackId,
          limit: 200,
          search: workoutFilters.search || undefined,
          category: workoutFilters.category || undefined,
          scoring_type: workoutFilters.scoring_type || undefined,
          date_from: workoutFilters.date_from || undefined,
          date_to: workoutFilters.date_to || undefined,
          equipment: workoutFilters.equipment || undefined,
        });
        if (response.success && response.data) {
          setTrackWorkouts(response.data.workouts as SpecialtyWorkout[]);
        }
      } catch (err) {
        console.error('Failed to fetch track workouts:', err);
        toast.error('Failed to load workouts');
      } finally {
        setIsLoadingWorkouts(false);
      }
    },
    [adminApi, workoutFilters]
  );

  // Re-fetch when filters change
  useEffect(() => {
    if (selectedTrackId) {
      fetchTrackWorkouts(selectedTrackId);
    }
  }, [selectedTrackId, fetchTrackWorkouts]);

  const handleSelectTrack = (trackId: string) => {
    setCalendarDateFilter(null);
    if (selectedTrackId === trackId) {
      setSelectedTrackId(null);
      setTrackWorkouts([]);
      setWorkoutFilters(emptyFilterValues);
    } else {
      setSelectedTrackId(trackId);
      setWorkoutFilters(emptyFilterValues);
      setExpandedWorkoutId(null);
    }
  };

  const handleCalendarDateClick = (date: string) => {
    setCalendarDateFilter(date);
    setWorkoutViewMode('table');
    setWorkoutFilters((prev) => ({ ...prev, date_from: date, date_to: date }));
  };

  const handleWorkoutFiltersChange = (nextFilters: FilterValues) => {
    setCalendarDateFilter((currentDate) =>
      currentDate && nextFilters.date_from === currentDate && nextFilters.date_to === currentDate
        ? currentDate
        : null
    );
    setWorkoutFilters(nextFilters);
  };

  const handleDeleteWorkout = async (workoutId: string) => {
    if (!confirm('Delete this workout? This cannot be undone.')) return;
    try {
      const response = await adminApi.deleteWorkout(workoutId);
      if (response.success) {
        toast.success('Workout deleted');
        if (selectedTrackId) fetchTrackWorkouts(selectedTrackId);
        fetchTracks();
      } else {
        toast.error(response.error || 'Failed to delete workout');
      }
    } catch {
      toast.error('Failed to delete workout');
    }
  };

  const selectedTrack = tracks.find((t) => t.id === selectedTrackId);
  const sortedTrackWorkouts = [...trackWorkouts].sort((a, b) =>
    (b.scheduled_date || '').localeCompare(a.scheduled_date || '')
  );

  return (
    <DashboardLayout>
      {/* Header */}
      <div className="mb-8 flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gradient-gold">Specialty</h1>
          <p className="mt-1 text-muted-foreground">
            Manage specialty programming and third-party programmes.
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            className="gap-2"
            onClick={() => {
              setShowUploadModal(true);
              csvImport.reset();
              setUploadTrackId('');
            }}
          >
            <Upload className="h-4 w-4" />
            Bulk Upload
          </Button>
          <Button className="gap-2" onClick={handleAddTrack}>
            <Plus className="h-4 w-4" />
            Add Specialty
          </Button>
        </div>
      </div>

      {/* Error State */}
      {error && (
        <Card className="mb-6 border-destructive">
          <CardContent className="py-4">
            <div className="flex items-center gap-2 text-destructive">
              <AlertCircle className="h-5 w-5" />
              <span>{error}</span>
              <Button variant="outline" size="sm" className="ml-auto" onClick={fetchTracks}>
                Retry
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tracks Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-primary" />
            All Specialties
            <Badge variant="secondary" className="ml-2">
              {tracks.length} specialties
            </Badge>
          </CardTitle>
          <CardDescription>Specialty programmes and third-party programming</CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : tracks.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <div className="rounded-full bg-muted p-4 mb-4">
                <Layers className="h-8 w-8 text-muted-foreground" />
              </div>
              <h3 className="text-lg font-semibold mb-1">No specialties found</h3>
              <p className="text-muted-foreground max-w-md">
                Create your first specialty programme to get started.
              </p>
              <Button className="mt-4" onClick={handleAddTrack}>
                <Plus className="h-4 w-4 mr-2" />
                Add Specialty
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10">
                    <Checkbox
                      checked={selection.isAllSelected(tracks.map((t) => t.id))}
                      onCheckedChange={(checked) => {
                        if (checked) {
                          selection.selectAll(tracks.map((t) => t.id));
                        } else {
                          selection.deselectAll();
                        }
                      }}
                    />
                  </TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Workouts</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {tracks.map((track) => (
                  <TableRow key={track.id} className="animate-fade-in">
                    <TableCell>
                      <Checkbox
                        checked={selection.isSelected(track.id)}
                        onCheckedChange={() => selection.toggle(track.id)}
                      />
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        {track.logo_url ? (
                          <img
                            src={getLogoUrl(track.id)}
                            alt={track.name}
                            className="h-8 w-8 rounded-lg object-contain"
                          />
                        ) : (
                          <div className="rounded-lg bg-primary/10 p-2">
                            <Layers className="h-4 w-4 text-primary" />
                          </div>
                        )}
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{track.name}</span>
                          {track.is_coming_soon && (
                            <Badge
                              variant="outline"
                              className="bg-amber-500/10 text-amber-500 border-amber-500/20 text-xs"
                            >
                              Coming Soon
                            </Badge>
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className="text-muted-foreground">
                        {track.description || 'No description'}
                      </span>
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="gap-1"
                        onClick={() => handleSelectTrack(track.id)}
                      >
                        <Badge variant="outline">{track.workout_count} workouts</Badge>
                        <ChevronDown
                          className={`h-3 w-3 transition-transform ${selectedTrackId === track.id ? 'rotate-180' : ''}`}
                        />
                      </Button>
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant="outline"
                        className={
                          track.is_active
                            ? 'bg-green-500/10 text-green-500 border-green-500/20'
                            : 'bg-red-500/10 text-red-500 border-red-500/20'
                        }
                      >
                        {track.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button variant="outline" size="sm" onClick={() => handleEditTrack(track)}>
                          <Pencil className="h-3 w-3 mr-1" />
                          Edit
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleToggleActive(track)}
                          className={
                            track.is_active
                              ? 'text-red-500 hover:text-red-600 hover:bg-red-500/10'
                              : 'text-green-500 hover:text-green-600 hover:bg-green-500/10'
                          }
                        >
                          {track.is_active ? (
                            <XCircle className="h-3 w-3" />
                          ) : (
                            <CheckCircle className="h-3 w-3" />
                          )}
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
        <BulkActionBar
          count={selection.count}
          onDelete={handleBulkDelete}
          onCancel={selection.deselectAll}
          isDeleting={isBulkDeleting}
        />
      </Card>

      {/* Specialty Workouts Panel */}
      {selectedTrackId && selectedTrack && (
        <Card className="mt-6">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Calendar className="h-5 w-5 text-primary" />
                {selectedTrack.name} Workouts
                <Badge variant="secondary" className="ml-2">
                  {sortedTrackWorkouts.length} workouts
                </Badge>
              </CardTitle>
              <div className="flex items-center gap-2">
                <div className="flex rounded-lg border border-border overflow-hidden">
                  <button
                    type="button"
                    onClick={() => {
                      setWorkoutViewMode('table');
                      setCalendarDateFilter(null);
                      setWorkoutFilters((prev) => ({ ...prev, date_from: '', date_to: '' }));
                    }}
                    className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                      workoutViewMode === 'table'
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-card hover:bg-accent text-muted-foreground'
                    }`}
                  >
                    Table
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setWorkoutViewMode('calendar');
                      setCalendarDateFilter(null);
                      const now = new Date();
                      const y = now.getFullYear();
                      const m = now.getMonth();
                      const firstDay = `${y}-${String(m + 1).padStart(2, '0')}-01`;
                      const lastDay = `${y}-${String(m + 1).padStart(2, '0')}-${new Date(y, m + 1, 0).getDate()}`;
                      setWorkoutFilters((prev) => ({
                        ...prev,
                        date_from: firstDay,
                        date_to: lastDay,
                      }));
                    }}
                    className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                      workoutViewMode === 'calendar'
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-card hover:bg-accent text-muted-foreground'
                    }`}
                  >
                    Calendar
                  </button>
                </div>
                <Button variant="ghost" size="sm" onClick={() => setSelectedTrackId(null)}>
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {calendarDateFilter && (
              <div className="mb-4">
                <CalendarDateFilter
                  date={calendarDateFilter}
                  onClear={() => {
                    setCalendarDateFilter(null);
                    setWorkoutFilters((prev) => ({ ...prev, date_from: '', date_to: '' }));
                  }}
                />
              </div>
            )}
            <FilterBar
              config={{
                showSearch: true,
                showScoring: true,
                showDate: true,
                showEquipment: true,
              }}
              values={workoutFilters}
              onChange={handleWorkoutFiltersChange}
              resultCount={sortedTrackWorkouts.length}
            />

            {isLoadingWorkouts ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
              </div>
            ) : workoutViewMode === 'calendar' ? (
              <CalendarView
                workouts={sortedTrackWorkouts}
                onDayClick={handleCalendarDateClick}
                onWorkoutClick={(w) => handleCalendarDateClick(getCalendarDate(w.scheduled_date))}
                onMonthChange={(y, m) => {
                  const firstDay = `${y}-${String(m + 1).padStart(2, '0')}-01`;
                  const lastDay = `${y}-${String(m + 1).padStart(2, '0')}-${new Date(y, m + 1, 0).getDate()}`;
                  setCalendarDateFilter(null);
                  setWorkoutFilters((prev) => ({ ...prev, date_from: firstDay, date_to: lastDay }));
                }}
              />
            ) : sortedTrackWorkouts.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                No workouts found for this specialty.
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Workout</TableHead>
                    <TableHead>Scoring</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortedTrackWorkouts.map((w) => (
                    <React.Fragment key={w.id}>
                      <TableRow
                        className="cursor-pointer hover:bg-accent/50"
                        onClick={() =>
                          setExpandedWorkoutId(expandedWorkoutId === w.id ? null : w.id)
                        }
                      >
                        <TableCell className="text-sm">
                          {w.scheduled_date && w.scheduled_date !== '9999-12-31'
                            ? new Date(`${w.scheduled_date}T00:00:00`).toLocaleDateString('en-GB', {
                                day: 'numeric',
                                month: 'short',
                                year: 'numeric',
                              })
                            : '—'}
                        </TableCell>
                        <TableCell>
                          <p className="font-medium">{w.name}</p>
                          <WorkoutCreatorMetadata
                            attribution={w.attribution}
                            created_at={w.created_at}
                            created_by_username={w.created_by_username}
                            className="mt-1"
                          />
                        </TableCell>
                        <TableCell>
                          <Badge variant="secondary">{w.scoring_type}</Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-destructive hover:text-destructive hover:bg-destructive/10"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteWorkout(w.id);
                            }}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                      {expandedWorkoutId === w.id && (
                        <TableRow>
                          <TableCell colSpan={4} className="p-0">
                            <WorkoutDetail workout={w} onClose={() => setExpandedWorkoutId(null)} />
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      )}

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <Card className="w-full max-w-md mx-4 animate-fade-in">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <Layers className="h-5 w-5 text-primary" />
                  {isEditing ? 'Edit Specialty' : 'Add Specialty'}
                </CardTitle>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setShowModal(false);
                    resetForm();
                  }}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
              <CardDescription>
                {isEditing ? 'Update specialty details' : 'Create a new specialty programme'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSave} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="track-name">Name *</Label>
                  <Input
                    id="track-name"
                    placeholder="e.g., Mayhem, PRVN, HWPO"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="track-description">Description</Label>
                  <textarea
                    id="track-description"
                    className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                    placeholder="Brief description of this specialty programme..."
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                  />
                </div>

                <div className="space-y-2">
                  <Label>Logo</Label>
                  <div className="flex items-center gap-3">
                    {/* Preview */}
                    {logoPreview ? (
                      <img
                        src={logoPreview}
                        alt="Logo preview"
                        className="h-12 w-12 rounded-lg object-contain border border-border"
                      />
                    ) : isEditing && editingTrack?.logo_url ? (
                      <img
                        src={getLogoUrl(editingTrack.id)}
                        alt="Current logo"
                        className="h-12 w-12 rounded-lg object-contain border border-border"
                      />
                    ) : (
                      <div className="flex h-12 w-12 items-center justify-center rounded-lg border border-dashed border-border">
                        <ImageIcon className="h-5 w-5 text-muted-foreground" />
                      </div>
                    )}
                    <div className="flex-1">
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/png,image/jpeg,image/webp,image/svg+xml"
                        onChange={handleLogoSelect}
                        className="hidden"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => fileInputRef.current?.click()}
                      >
                        <Upload className="h-3 w-3 mr-1" />
                        {logoPreview || (isEditing && editingTrack?.logo_url)
                          ? 'Change Logo'
                          : 'Upload Logo'}
                      </Button>
                      <p className="text-xs text-muted-foreground mt-1">
                        PNG, JPEG, WebP or SVG. Max 2MB.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="track-coming-soon"
                    checked={isComingSoon}
                    onChange={(e) => setIsComingSoon(e.target.checked)}
                    className="h-4 w-4"
                  />
                  <Label htmlFor="track-coming-soon">Mark as "Coming Soon"</Label>
                </div>

                {isEditing && (
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="track-active"
                      checked={isActive}
                      onChange={(e) => setIsActive(e.target.checked)}
                      className="h-4 w-4"
                    />
                    <Label htmlFor="track-active">Active</Label>
                  </div>
                )}

                <div className="flex gap-2 pt-4">
                  <Button
                    variant="outline"
                    className="flex-1"
                    onClick={() => {
                      setShowModal(false);
                      resetForm();
                    }}
                    type="button"
                  >
                    Cancel
                  </Button>
                  <Button className="flex-1" type="submit" disabled={isSaving || isUploadingLogo}>
                    {isUploadingLogo ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Uploading Logo...
                      </>
                    ) : isSaving ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Saving...
                      </>
                    ) : isEditing ? (
                      'Save Changes'
                    ) : (
                      'Add Specialty'
                    )}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}

      {showUploadModal && (
        <CsvBulkUploadDialog
          title="Bulk Upload Specialty Workouts"
          description="Upload a CSV file to add workouts to a specialty programme."
          destination="Specialty"
          csvContent={csvImport.csvContent}
          fileName={csvImport.fileName}
          result={csvImport.result}
          isUploading={csvImport.isUploading}
          onFileSelect={csvImport.onFileSelect}
          onUpload={handleBulkUpload}
          onClose={() => {
            setShowUploadModal(false);
            csvImport.reset();
          }}
          uploadLabel="Upload Workouts"
          uploadDisabled={!uploadTrackId}
          fileLabel="CSV File *"
          compactResult
          doneLabel="Close"
          doneVariant="outline"
          showUploadRowCount={false}
          previewMode="count"
          secondaryActionFirst
          maxWidth="lg"
          maxHeight="80vh"
          selector={
            <div className="space-y-2">
              <Label>Specialty Programme *</Label>
              <select
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                value={uploadTrackId}
                onChange={(event) => setUploadTrackId(event.target.value)}
              >
                <option value="">Select a programme...</option>
                {tracks.map((track) => (
                  <option key={track.id} value={track.id}>
                    {track.name} ({track.workout_count} workouts)
                  </option>
                ))}
              </select>
            </div>
          }
          instructions={
            <p>
              Required columns: name (or description), scoring_type (or metrics). Optional:
              category, equipment, movements, coach_notes, duration, instructions, video_url.
            </p>
          }
        />
      )}
    </DashboardLayout>
  );
}
